/* ================================================================
   BRITTEN CUSTOM: PDP "Other veils you might love" row (v2)
   ================================================================
   Added:       5 October 2026
   Author:      Claude (for Matthew)
   Location:    assets/britten-recs.js  (+ assets/britten-recs.css)
   Loaded by:   sections/product__recommendations.liquid (defer, only when
                settings.britten_recs_v2 is on)

   PURPOSE:
   Draws the row at the bottom of every product page: four cards, on a veil page
   also "change one thing" buttons and a "Still deciding?" shortlist of the veils
   she saved or looked at. Spec: docs/pdp-recommendations-v2-design-brief.md.

   HOW IT WORKS:
   1. The section prints a root, [data-brecs], holding a loading skeleton and a
      JSON payload ({veil, cur, recs}, fields in snippets/britten-recs-item.liquid).
      z__jsRecommendedProducts.js injects it after load and fires
      britten:recs:injected. This file starts on that event, and also on load if
      the root is already there.
   2. A non-veil page draws at once from the payload. A veil page waits until the
      row is within 1500px of the screen, then fetches the veil index once
      ({root}collections/wedding-veils?view=britten-recs-index, kept in
      sessionStorage for 20 minutes). It keeps the skeleton until the index and
      Britten Core are both ready, and draws without the buttons and the
      shortlist if the index fails or takes over 4 seconds. One draw, no jumps.
   3. Everything the shopper sees is a locale key (britten.js.recs.*).
   4. Save buttons are the core's own [data-britten-save] buttons (surface
      "recs"): the core wires them, keeps aria-pressed right and shows its
      "Saved . View saved" toast. Nothing here saves anything itself.
   5. Events go out as britten:metric (pdp_recs_card_seen, pdp_recs_click,
      pdp_recs_chip, pdp_recs_compare). A click on a card also writes
      sessionStorage britten:recs:src, which britten-pdp-metrics.liquid reads to
      mark the add to bag as coming from this row.

   THEME DEPENDENCY:
   BrittenI18n (string table), BrittenCore + BrittenCoreConfig (optional),
   localStorage britten:viewed:v1 (britten-viewed-tracker.liquid), Shopify.routes.root.

   CHANGELOG:
   - 5 Oct 2026: Created.
   ================================================================ */
(function () {
  'use strict';
  var W = window, D = document;
  if (W.BrittenRecs) { return; }
  W.BrittenRecs = 1;

  var I18N = W.BrittenI18n;
  // i18n-keys: js.recs.chip_blusher, js.recs.chip_cut, js.recs.chip_lace, js.recs.chip_similar, js.recs.chip_single, js.recs.chip_social
  // i18n-keys: js.recs.compare, js.recs.from, js.recs.group_label, js.recs.heading_other, js.recs.heading_veil
  // i18n-keys: js.recs.label_saved, js.recs.label_viewed, js.recs.name_fallback, js.recs.proof, js.recs.save, js.recs.save_aria, js.recs.saved_aria
  // i18n-keys: js.recs.match_blusher, js.recs.match_cut, js.recs.match_lace, js.recs.match_similar, js.recs.match_single, js.recs.match_social
  // i18n-keys: js.recs.still_cut, js.recs.still_horsehair, js.recs.still_lace, js.recs.still_pencil, js.recs.still_ribbon, js.recs.still_single, js.recs.still_two
  // i18n-keys: js.recs.sl_heading, js.recs.sl_sub, js.recs.this_veil
  var NS = 'js.recs.';
  function t(key, vars) { return I18N && I18N.t ? I18N.t(NS + key, vars) : ''; }

  var BRIDES_MIN = 5;       // the "Worn by N Real Brides" line and the "most worn" chip
  var SHOW = 4;             // cards in the grid
  var WIDTHS = [240, 360, 480, 600, 720, 900];
  var SIZES = '(min-width: 750px) 25vw, 50vw';
  var ICON = '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M7 4h10a1 1 0 0 1 1 1v15l-6-4.2L6 20V5a1 1 0 0 1 1-1z"/></svg>';
  var PROPER = /\b(french|italian|spanish|russian|english|juliet|britten)\b/g;

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) { return '&#' + c.charCodeAt(0) + ';'; });
  }
  function each(list, fn) { Array.prototype.forEach.call(list || [], fn); }
  function closest(el, sel) { return el && el.closest ? el.closest(sel) : null; }
  function metric(name, data) {
    try { D.dispatchEvent(new CustomEvent('britten:metric', { detail: { name: name, data: data } })); } catch (e) { /* analytics never breaks the page */ }
  }
  function rootUrl() {
    var r = (W.Shopify && W.Shopify.routes && W.Shopify.routes.root) || (W.BrittenCoreConfig && W.BrittenCoreConfig.rootUrl) || '/';
    return String(r).replace(/\/*$/, '/');
  }
  function reduced() { return !!(W.matchMedia && W.matchMedia('(prefers-reduced-motion: reduce)').matches); }

  // ---- Titles: "Descriptor - 'Name'" and the unnamed "Cut edge drop wedding veil - Various Materials" ----
  function sentence(s) {
    var w = String(s || '').replace(/\s+/g, ' ').replace(/^ | $/g, '').split(' '), n = 0, c = 0, i, l;
    s = w.join(' ');
    for (i = 0; i < w.length; i++) {
      l = w[i].replace(/[^A-Za-zÀ-ɏ]/g, '');
      if (l.length > 3) { n++; if (l.charAt(0) !== l.charAt(0).toLowerCase()) { c++; } }
    }
    // Only a Title Case title is lowered; one already in sentence case keeps its capitals ("French lace").
    if (n > 1 && c === n) {
      s = s.toLowerCase().replace(PROPER, function (m) { return m.charAt(0).toUpperCase() + m.slice(1); });
    }
    return s.charAt(0).toUpperCase() + s.slice(1);
  }
  function parse(title) {
    // Quoted name: 'Layla' in English, «Layla» in Spanish (curly quotes too).
    var m = /^(.*?)\s*[-–—]\s*['‘’"“”«]([^'‘’"“”«»]+)['‘’"“”»]\s*$/.exec(title);
    if (m) { return { name: m[2], desc: sentence(m[1]), named: 1 }; }
    // An unquoted single-word name at the end ("… Extra Wide- Blair").
    m = /^(.*?)\s*[-–—]\s*([A-Z][a-zà-ÿ]{2,})\s*$/.exec(title);
    if (m) { return { name: m[2], desc: sentence(m[1]), named: 1 }; }
    m = /^(.*?)\s+-\s+(.*)$/.exec(title);
    if (m) { return { name: sentence(m[1]), desc: m[2], named: 0 }; }
    return { name: sentence(title), desc: '', named: 0 };
  }

  // ---- Images: the payload carries the file URL with no width; the CDN resizes on &width=N ----
  function sized(base, w) { return base + (base.indexOf('?') < 0 ? '?' : '&') + 'width=' + w; }
  function srcset(base) {
    return WIDTHS.map(function (w) { return sized(base, w) + ' ' + w + 'w'; }).join(', ');
  }

  // ---- Card ----
  function saveAttrs(v, surface) {
    return ' data-britten-save data-kind="product" data-surface="' + surface + '" data-product-id="' + v.id + '" data-variant-id="' + v.vid +
      '" data-handle="' + esc(v.handle) + '" data-title="' + esc(v.title) + '" data-image="' + esc(v.img ? sized(v.img, 600) : '') +
      '" data-price="' + v.cents + '" aria-pressed="false"';
  }
  function card(v, pos, set) {
    var p = parse(v.title), nm = esc(p.name);
    return '<div class="brecs-card' + (p.named ? '' : ' brecs-card--unnamed') + '" data-h="' + esc(v.handle) + '" data-pos="' + pos + '" data-set="' + set + '">' +
      '<a class="brecs-card__link" href="' + esc(v.url) + '">' +
      '<span class="brecs-card__media">' +
      (v.img ? '<img class="brecs-card__img" src="' + esc(sized(v.img, 480)) + '" srcset="' + esc(srcset(v.img)) + '" sizes="' + SIZES + '" alt="' + esc(v.title) + '" loading="lazy" decoding="async">' : '') +
      (v.img && v.img2 ? '<img class="brecs-card__img brecs-card__img--alt" data-srcset="' + esc(srcset(v.img2)) + '" sizes="' + SIZES + '" alt="" aria-hidden="true" decoding="async">' : '') +
      (v.badge ? '<span class="brecs-card__badge">' + esc(v.badge) + '</span>' : '') +
      '</span>' +
      '<span class="brecs-card__body">' +
      '<span class="brecs-card__titles"><span class="brecs-card__name">' + nm + '</span>' + (p.desc ? '<span class="brecs-card__desc">' + esc(p.desc) + '</span>' : '') + '</span>' +
      '<span class="brecs-card__price">' + (v.from ? '<em class="brecs-card__from">' + esc(t('from')) + '</em> ' : '') + esc(v.price) + '</span>' +
      (v.brides >= BRIDES_MIN ? '<span class="brecs-card__proof">' + esc(t('proof', { brides: v.brides })) + '</span>' : '') +
      '</span></a>' +
      '<button type="button" class="britten-core-save yb-save yb-save--card brecs-save brecs-card__save"' + saveAttrs(v, 'recs') +
      ' data-brecs-name="' + nm + '" aria-label="' + esc(t('save_aria', { name: p.name })) + '">' + ICON + '</button>' +
      '</div>';
  }
  function cards(list, set) {
    return list.map(function (v, i) { return card(v, i + 1, set); }).join('');
  }

  // ---- "Still deciding?" ----
  function slItem(e, first) {
    var v = e.v, p = parse(v.title), isThis = first, src = isThis ? 'this' : e.src;
    var label = t(src === 'this' ? 'this_veil' : (src === 'saved' ? 'label_saved' : 'label_viewed'));
    var save = src === 'viewed';
    return '<li class="brecs-sl__item' + (save ? '' : ' brecs-sl__item--nosave') + '" data-h="' + esc(v.handle) + '" data-src="' + src + '">' +
      '<a class="brecs-sl__thumb" href="' + esc(v.url) + '" tabindex="-1" aria-hidden="true">' + (v.img ? '<img src="' + esc(sized(v.img, 160)) + '" alt="" loading="lazy" decoding="async">' : '') + '</a>' +
      '<span class="brecs-sl__label">' + esc(label) + '</span>' +
      '<a class="brecs-sl__link" href="' + esc(v.url) + '"><span class="brecs-sl__name">' + esc(p.name) + '</span>' + (p.desc ? '<span class="brecs-sl__desc">' + esc(p.desc) + '</span>' : '') + '</a>' +
      '<span class="brecs-sl__pricerow"><span class="brecs-sl__price">' + (v.from ? esc(t('from')) + ' ' : '') + esc(v.price) + '</span>' +
      (save ? '<button type="button" class="britten-core-save yb-save brecs-save brecs-sl__save"' + saveAttrs(v, 'recs') + ' data-brecs-name="' + esc(p.name) + '" aria-label="' + esc(t('save_aria', { name: p.name })) + '">' +
        ICON + '<span>' + esc(t('save')) + '</span></button>' : '') +
      '</span></li>';
  }
  function compareUrl(S) {
    var cfg = W.BrittenCoreConfig, base = rootUrl().replace(/\/$/, '') + ((cfg && cfg.savedPath) || '/pages/saved');
    return base + '?compare=' + [S.cur.handle].concat(S.sl.map(function (e) { return e.v.handle; })).map(encodeURIComponent).join(',');
  }
  function slHtml(S) {
    return '<div class="brecs-sl"><div class="brecs__head"><h2 class="brecs__title">' + esc(t('sl_heading')) + '</h2><span class="brecs__rule"></span></div>' +
      '<p class="brecs-sl__sub">' + esc(t('sl_sub')) + '</p><div class="brecs-sl__panel"><ul class="brecs-sl__list">' +
      slItem({ v: S.cur }, true) + S.sl.map(function (e) { return slItem(e, false); }).join('') +
      '</ul><div class="brecs-sl__cta"><a class="brecs-sl__compare" href="' + esc(compareUrl(S)) + '">' + esc(t('compare')) + '</a></div></div></div>';
  }

  // ---- Matching (veil pages, needs the index) ----
  function fam(e) { return e === 'semi' ? 'lace' : e; }      // semi lace counts as lace
  function rank(S, chip) {
    var c = S.cur, ef = fam(c.edge), out = [];
    S.pool.forEach(function (v, i) {
      if (!chip.test(v)) { return; }
      var k = 0;   // 0 = the kept detail is known and matches, 1 = unknown on the candidate (allowed, ranked after)
      if (chip.keep === 'tier' && c.tier) { if (!v.tier) { k = 1; } else if (v.tier !== c.tier) { return; } }
      if (chip.keep === 'edge' && ef) { if (!fam(v.edge)) { k = 1; } else if (fam(v.edge) !== ef) { return; } }
      out.push({ v: v, k: k, s: c.style && v.style === c.style ? 1 : 0, m: c.material && v.material === c.material ? 1 : 0, i: i });
    });
    out.sort(function (a, b) { return a.k - b.k || b.s - a.s || b.m - a.m || b.v.brides - a.v.brides || a.i - b.i; });
    return out.map(function (x) { return x.v; });
  }
  function buildSets(S) {
    var c = S.cur, ef = fam(c.edge), defs = [], gone = {};
    gone[c.handle] = 1;
    S.sl.forEach(function (e) { gone[e.v.handle] = 1; });
    S.sets = { similar: S.recs.filter(function (v) { return !gone[v.handle]; }).slice(0, SHOW) };
    S.chips = [];
    if (!S.byH) { return; }
    S.pool = S.list.filter(function (v) { return !gone[v.handle]; });
    if (ef === 'lace') { defs.push({ key: 'cut', keep: 'tier', test: function (v) { return v.edge === 'cut'; } }); }
    if (ef === 'cut') { defs.push({ key: 'lace', keep: 'tier', test: function (v) { return fam(v.edge) === 'lace'; } }); }
    if (c.tier === 'two') { defs.push({ key: 'single', keep: 'edge', test: function (v) { return v.tier === 'single'; } }); }
    if (c.tier === 'single') { defs.push({ key: 'blusher', keep: 'edge', test: function (v) { return v.tier === 'two'; } }); }
    defs.forEach(function (d) {
      var l = rank(S, d).slice(0, SHOW);
      if (l.length >= 2) { S.sets[d.key] = l; S.chips.push(d.key); }
    });
    var best = S.pool.filter(function (v) { return v.brides >= BRIDES_MIN; }).map(function (v, i) { return { v: v, i: i }; });
    best.sort(function (a, b) { return b.v.brides - a.v.brides || a.i - b.i; });
    best = best.slice(0, SHOW).map(function (x) { return x.v; });
    if (best.length >= 2) { S.sets.social = best; S.chips.push('social'); }
  }
  function matchLine(S) {
    if (!S.byH) { return ''; }
    var c = S.cur, key = S.sel, nm = S.named || t('name_fallback'), keep, out;
    if (key === 'similar') { return t('match_similar', { name: nm }); }
    if (key === 'social') { return t('match_social'); }
    out = t('match_' + key, { name: nm });
    if (key === 'lace' || key === 'cut') {
      keep = c.tier ? 'still_' + c.tier : '';
    } else {
      keep = { cut: 'still_cut', lace: 'still_lace', semi: 'still_lace', pencil: 'still_pencil', horsehair: 'still_horsehair', ribbon: 'still_ribbon' }[c.edge] || '';
    }
    return keep ? out + ' ' + t(keep) : out;
  }

  // ---- Draw ----
  function chipsHtml(S) {
    if (!S.chips.length) { return ''; }
    return '<div class="brecs__chips" role="group" aria-label="' + esc(t('group_label')) + '">' +
      ['similar'].concat(S.chips).map(function (k) {
        return '<button type="button" class="brecs-chip" data-chip="' + k + '" aria-pressed="' + (k === S.sel) + '">' + esc(t('chip_' + k)) + '</button>';
      }).join('') + '</div>';
  }
  function hideRow(root) {
    var box = closest(root, '[data-product-recommendations-container]');
    root.hidden = true;
    if (box) { box.style.display = 'none'; }
  }
  function draw(root, S) {
    var m = matchLine(S), html;
    if (!S.sets.similar.length) { hideRow(root); return; }
    html = (S.sl.length ? slHtml(S) : '') +
      '<div class="brecs__head"><h2 class="brecs__title">' + esc(t(S.veil ? 'heading_veil' : 'heading_other')) + '</h2><span class="brecs__rule"></span></div>' +
      (m ? '<p class="brecs__match" aria-live="polite">' + esc(m) + '</p>' : '') + chipsHtml(S) +
      '<div class="brecs__grid">' + cards(S.sets.similar, 'similar') + '</div>';
    root.innerHTML = html;
    root.removeAttribute('aria-busy');
    watch(root, S);
  }
  function watch(root, S) {
    if (!S.io) { return; }
    each(root.querySelectorAll('.brecs-card'), function (el) { S.io.observe(el); });
  }

  // ---- The shopper's own veils ----
  function shortlist(S) {
    var out = [], seen = {}, core = W.BrittenCore, viewed = [];
    seen[S.cur.handle] = 1;
    function add(v, src) {
      if (v && !seen[v.handle] && out.length < 2) { seen[v.handle] = 1; out.push({ v: v, src: src }); }
    }
    try {
      if (core && core.items) {
        core.items.list({ kind: 'product' }).forEach(function (it) { add(S.byH[it.handle] || S.byId[it.product_id], 'saved'); });
      }
    } catch (e) { /* the list is a nicety */ }
    try { viewed = JSON.parse(W.localStorage.getItem('britten:viewed:v1') || '[]'); } catch (e) { viewed = []; }
    (viewed || []).forEach(function (x) { if (x && x.h) { add(S.byH[x.h], 'viewed'); } });
    return out;
  }

  // ---- The veil index ----
  function loadIndex(cb) {
    var url = rootUrl() + 'collections/wedding-veils?view=britten-recs-index', key = 'britten:recs:idx:' + url, all = [], page = 1, hit;
    try {
      hit = JSON.parse(W.sessionStorage.getItem(key));
      if (hit && hit.d && hit.d.length && Date.now() - hit.t < 1200000) { cb(hit.d); return; }
    } catch (e) { /* no cache */ }
    if (!W.fetch) { cb(null); return; }
    (function next() {
      W.fetch(url + (page > 1 ? '&page=' + page : ''), { credentials: 'same-origin' }).then(function (r) {
        if (!r.ok) { throw new Error('index ' + r.status); }
        return r.text();
      }).then(function (txt) {
        var more = 0;
        JSON.parse(txt).forEach(function (x) { if (x && x.id) { all.push(x); } else if (x && x.next) { more = x.next; } });
        if (more && page < 4) { page = more; next(); return; }
        if (!all.length) { cb(null); return; }
        try { W.sessionStorage.setItem(key, JSON.stringify({ t: Date.now(), d: all })); } catch (e) { /* storage full or off */ }
        cb(all);
      }).catch(function () { cb(null); });
    })();
  }
  function whenCore(cb) {
    // Core present, or the core is off on this page (no config): nothing to wait for.
    if (W.BrittenCore || !W.BrittenCoreConfig) { cb(); return; }
    D.addEventListener('britten:core:ready', function once() { D.removeEventListener('britten:core:ready', once); cb(); });
  }
  function whenNear(el, cb) {
    if (!('IntersectionObserver' in W)) { cb(); return; }
    var io = new W.IntersectionObserver(function (es) {
      if (es[0] && es[0].isIntersecting) { io.disconnect(); cb(); }
    }, { rootMargin: '1500px 0px 1500px 0px' });
    io.observe(el);
  }

  // ---- Behaviour ----
  function loadAlts(root) {
    each(root.querySelectorAll('.brecs-card__img--alt[data-srcset]'), function (img) { img.srcset = img.getAttribute('data-srcset'); img.removeAttribute('data-srcset'); });
  }
  function swap(root, S) {
    var grid = root.querySelector('.brecs__grid'), line = root.querySelector('.brecs__match');
    if (!grid) { return; }
    grid.innerHTML = cards(S.sets[S.sel], S.sel);
    if (line) { line.textContent = matchLine(S); }
    if (S.alts) { loadAlts(root); }
    watch(root, S);
    grid.className = 'brecs__grid';
  }
  function pick(root, S, key) {
    var grid = root.querySelector('.brecs__grid');
    if (key === S.sel) { if (key === 'similar') { return; } key = 'similar'; }
    S.sel = key;
    each(root.querySelectorAll('.brecs-chip'), function (b) { b.setAttribute('aria-pressed', b.getAttribute('data-chip') === key ? 'true' : 'false'); });
    metric('pdp_recs_chip', { chip: key });
    clearTimeout(S.timer);
    if (reduced()) { swap(root, S); return; }
    grid.className = 'brecs__grid brecs__grid--fading';
    S.timer = setTimeout(function () { swap(root, S); }, 180);
  }
  function bind(root, S) {
    root.addEventListener('click', function (e) {
      var a = closest(e.target, '.brecs-card__link'), chip = closest(e.target, '.brecs-chip'), c, d;
      if (a) {
        c = a.parentNode;
        d = { pos: +c.getAttribute('data-pos'), set: c.getAttribute('data-set'), handle: c.getAttribute('data-h') };
        metric('pdp_recs_click', d);
        try { W.sessionStorage.setItem('britten:recs:src', JSON.stringify({ h: d.handle, set: d.set, pos: d.pos, t: Date.now() })); } catch (x) { /* fine */ }
      } else if (chip) {
        pick(root, S, chip.getAttribute('data-chip'));
      } else if (closest(e.target, '.brecs-sl__compare')) {
        metric('pdp_recs_compare', { count: 1 + S.sl.length });
      }
    });
    // The second photo is only fetched once a mouse is over the row (a phone never needs it).
    root.addEventListener('mouseover', function () {
      if (S.alts) { return; }
      S.alts = 1;
      loadAlts(root);
    });
    // The core flips aria-pressed on every save button, then fires this. Follow it.
    D.addEventListener('britten:items:change', function () {
      each(root.querySelectorAll('.brecs-card__save'), function (b) {
        b.setAttribute('aria-label', t(b.getAttribute('aria-pressed') === 'true' ? 'saved_aria' : 'save_aria', { name: b.getAttribute('data-brecs-name') }));
      });
      each(root.querySelectorAll('.brecs-sl__item[data-src="viewed"]'), function (li) {
        var b = li.querySelector('.brecs-sl__save');
        if (!b || b.getAttribute('aria-pressed') !== 'true') { return; }
        li.setAttribute('data-src', 'saved');
        li.className += ' brecs-sl__item--nosave';
        li.querySelector('.brecs-sl__label').textContent = t('label_saved');
        b.parentNode.removeChild(b);
      });
    });
    if ('IntersectionObserver' in W) {
      S.io = new W.IntersectionObserver(function (es) {
        es.forEach(function (en) {
          var el = en.target, k;
          if (!en.isIntersecting) { return; }
          S.io.unobserve(el);
          k = el.getAttribute('data-set') + ':' + el.getAttribute('data-h');
          if (S.seen[k]) { return; }
          S.seen[k] = 1;
          metric('pdp_recs_card_seen', { pos: +el.getAttribute('data-pos'), set: el.getAttribute('data-set'), handle: el.getAttribute('data-h') });
        });
      }, { threshold: 0.5 });
    }
  }

  // ---- Start ----
  function start(root, S, list) {
    var by = {}, byId = {};
    if (list) {
      S.list = list.filter(function (v) { return v.handle !== S.cur.handle; });
      list.forEach(function (v) { by[v.handle] = v; byId[v.id] = v; });
      S.byH = by;
      S.byId = byId;
      S.sl = shortlist(S);
    }
    buildSets(S);
    draw(root, S);
  }
  function init(root) {
    var S, data;
    if (root.getAttribute('data-brecs-on')) { return; }
    root.setAttribute('data-brecs-on', '1');
    try { data = JSON.parse(root.querySelector('[data-brecs-data]').textContent); } catch (e) { hideRow(root); return; }
    S = { veil: !!data.veil, cur: data.cur, recs: data.recs || [], sl: [], chips: [], sets: {}, sel: 'similar', seen: {}, named: '' };
    if (!S.cur || !S.recs.length) { hideRow(root); return; }
    data = parse(S.cur.title);
    S.named = data.named ? data.name : '';
    bind(root, S);
    if (!S.veil) { start(root, S, null); return; }
    whenNear(root, function () {
      var left = 2, done = false, list = null, timer;
      function fin() {
        if (done) { return; }
        done = true;
        clearTimeout(timer);
        start(root, S, list);
      }
      function step() { left -= 1; if (!left) { fin(); } }
      timer = setTimeout(fin, 4000);
      loadIndex(function (l) { list = l; step(); });
      whenCore(step);
    });
  }
  // The static copy of the section (hidden once the row is injected) is never drawn.
  function scan() { each(D.querySelectorAll('[data-brecs]'), function (r) { if (!closest(r, '.recommended-products-section')) { init(r); } }); }

  D.addEventListener('britten:recs:injected', scan);
  if (D.readyState === 'loading') { D.addEventListener('DOMContentLoaded', scan); } else { scan(); }
}());
